import type { FormBlockMetadataById } from 'models/metadata/block.definition';
import { ReactNode } from 'react';
import { connect } from 'react-redux';
import { FormIdProps } from 'shared/props';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import { dynamicFormTabRollbackedThunk, workspaceTabClonedThunk } from 'state/dynamic/dynamicForm.thunks';
import { getDynamicWorkflowStatus, isDynamicFormLoaded } from 'state/dynamic/selectors';
import type { DataById } from 'state/formData/formData.model';
import {
  getFormEnrichedData,
  getFormInputData,
  getFormPendingEnrichmentData,
  isPendingEnrichment,
} from 'state/formData/formData.selectors';
import {
  formInputDataChangedThunk,
  refreshScheduleThunk,
  resetDefaultDatesThunk,
} from 'state/formData/formData.thunks';
import { getBlocksMetadata, getFieldsMetadata, getProductDescription } from 'state/forms/metadata/selectors';
import type { ProductGraph } from 'state/product-description/model';
import {
  getDynamicFormInfos,
  getDynamicFormValidationErrors,
  getDynamicPreviousQuoteIsLoading,
} from 'state/selectors/common/dynamicForm';
import { getIsUserInternal } from 'state/selectors/common/user';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';
import { workspaceResetTabRequestedThunk } from 'state/workspaces/workspace.thunks';

export interface DynamicFormOwnProps extends FormIdProps {
  hideCloneButton?: boolean;
  hideOpenInBatchWorkspaceButton?: boolean;
  hideResetButton?: boolean;
  hidePrivateMode?: boolean;
  SidePanel: ReactNode;
}

export interface DynamicFormStateProps {
  productDescription?: ProductGraph;
  blocksMetadata: FormBlockMetadataById;
  fieldsMetadata: FieldMetadataById;
  enrichedData: DataById;
  inputData: DataById;
  pendingEnrichmentData: DataById;
  isLoading: boolean;
  validationErrors: readonly QuoteFormError[];
  isUserInternal: boolean;
  crossBrowserDirty: boolean;

  canReloadSession: boolean;

  canReset: boolean;
  canClone: boolean;

  isNotEditable: boolean;

  isTabFrozen: boolean;

  negotiationId?: string;
}

export interface DynamicFormDispatchProps {
  setFieldValue: (key: string, value: unknown) => void;
  onResetTabClicked: () => void;
  onRefreshDefaultDatesClicked: () => void;
  onRefreshScheduleClicked: () => void;
  onTabCloned: () => void;
  onRollback: (negotiationId: string) => void;
}

export type DynamicFormProps = DynamicFormOwnProps & DynamicFormStateProps & DynamicFormDispatchProps;

const EMPTY_OBJECT = {};
const mapStateToProps = (state: AppState, { formId }: DynamicFormOwnProps): DynamicFormStateProps => {
  const { crossBrowserDirty = false, negotiationId } = getDynamicFormInfos(state, formId) ?? {};
  const productDescription = getProductDescription(state, formId);
  const blocksMetadata = getBlocksMetadata(state, 'dynamic', formId) ?? EMPTY_OBJECT;
  const fieldsMetadata = getFieldsMetadata(state, 'dynamic', formId) ?? EMPTY_OBJECT;
  const enrichedData = getFormEnrichedData(state, formId);
  const inputData = getFormInputData(state, formId);
  const pendingEnrichmentData = getFormPendingEnrichmentData(state, formId);
  const isLoading = !isDynamicFormLoaded(state, formId);
  const isQuoting = getDynamicWorkflowStatus(state, formId) === 'QUOTING';
  const validationErrors = getDynamicFormValidationErrors(state, formId);
  const isUserInternal = getIsUserInternal(state);
  const isLoadingPreviousQuote = getDynamicPreviousQuoteIsLoading(state, formId);
  const isTabFrozen = getTabIsFrozen(state, formId) ?? false;
  const isFrozen = isQuoting || isTabFrozen;
  const isPerformingEnrichment = isPendingEnrichment(state, formId);

  const canReset = !isLoading && !isPerformingEnrichment && !isFrozen && !crossBrowserDirty && !isLoadingPreviousQuote;
  const canClone = !isLoading && !isPerformingEnrichment && !crossBrowserDirty;

  const canReloadSession = !isFrozen;

  const isNotEditable = isTabFrozen || crossBrowserDirty || isLoadingPreviousQuote;

  return {
    productDescription,
    blocksMetadata,
    fieldsMetadata,
    enrichedData,
    inputData,
    pendingEnrichmentData,
    isLoading,
    validationErrors,
    isUserInternal,
    crossBrowserDirty,
    canReset,
    canClone,
    canReloadSession,
    isNotEditable,
    isTabFrozen,
    negotiationId,
  };
};

function mapDispatchToProps(dispatch: ThunkDispatch, { formId }: DynamicFormOwnProps): DynamicFormDispatchProps {
  return {
    setFieldValue: (key: string, value: unknown) => {
      dispatch(formInputDataChangedThunk(formId, formId, key, value as Readonly<unknown>));
    },
    onResetTabClicked: () => {
      dispatch(workspaceResetTabRequestedThunk(formId, 'dynamic'));
    },
    onRefreshScheduleClicked: () => {
      dispatch(refreshScheduleThunk(formId, formId));
    },
    onRefreshDefaultDatesClicked: () => {
      dispatch(resetDefaultDatesThunk(formId, formId));
    },
    onTabCloned() {
      dispatch(workspaceTabClonedThunk(formId, 'dynamic', 'quote'));
    },
    onRollback(negotiationId) {
      dispatch(dynamicFormTabRollbackedThunk(formId, negotiationId));
    },
  };
}

export const connectDynamicForm = connect(mapStateToProps, mapDispatchToProps);
